import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Content,
  Header,
  LinkButton,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  entityRouteParams,
  entityRouteRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import { Grid, makeStyles, Tab, Tabs } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SyncIcon from '@material-ui/icons/Sync';
import SearchIcon from '@material-ui/icons/Search';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import {
  facetOptions,
  FacetKey,
  isChosen,
  ListFilters,
  ListRow,
  ListTab,
  readFilters,
  TABS,
  TypeChip,
  viewOf,
  withoutChipAxis,
  writeFilters,
} from './listFilters';
import { useCatalogList } from './useCatalogList';
import { useViewPreference } from './useViewPreference';
import { useListKeyboard } from './useListKeyboard';
import { FilterChips } from './FilterChips';
import { ListTable } from './ListTable';
import { ListCards } from './ListCards';
import {
  EmptyCatalog,
  EmptyMatch,
  EmptyMine,
  EmptyScope,
  EmptyStarred,
} from './EmptyList';

/**
 * The catalog list (specs/catalog-list.md): every component and API the
 * catalog holds, narrowed by whose it is, what kind of thing it is and what the
 * reader types, as a table or as cards — and moved through by keyboard.
 *
 * Everything the page is narrowed by is in its address, in the vocabulary the
 * stock catalog page used, so a link written for that page still arrives at
 * the list it meant (see listFilters). The one thing that is not is the table
 * or cards choice, which is the reader's preference rather than part of the
 * question (see useViewPreference).
 *
 * MUI v4 only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  tabs: {
    minHeight: 40,
    borderBottom: '1px solid var(--ui-border)',
    marginBottom: theme.spacing(2),
  },
  indicator: { backgroundColor: 'var(--ui-primary)' },
  tab: {
    minHeight: 40,
    minWidth: 0,
    padding: theme.spacing(0.5, 0),
    marginRight: theme.spacing(3),
    textTransform: 'none',
    fontSize: '0.9rem',
    color: 'var(--ui-text-muted)',
    opacity: 1,
    '&[aria-selected="true"]': { color: 'var(--ui-text)', fontWeight: 600 },
  },
  tabCount: {
    marginLeft: theme.spacing(0.25),
    fontWeight: 400,
    color: 'var(--ui-text-muted)',
    fontVariantNumeric: 'tabular-nums',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  tools: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '0 1 auto',
    minWidth: 0,
    [theme.breakpoints.down('xs')]: { flex: '1 1 100%' },
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    // A width of its own: sized by its content, the field is as wide as the
    // browser's default input and cuts its own placeholder off.
    flex: '1 1 auto',
    width: 300,
    minWidth: 0,
    height: 32,
    padding: theme.spacing(0, 1),
    borderRadius: 'var(--ui-radius)',
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text-muted)',
    '&:focus-within': { borderColor: 'var(--ui-primary)' },
    '& svg': { fontSize: 18 },
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: 'var(--ui-text)',
    font: 'inherit',
    fontSize: '0.875rem',
    '&::placeholder': { color: 'var(--ui-text-muted)', opacity: 0.8 },
    // The field's own clear button would sit beside the shortcut hint.
    '&::-webkit-search-cancel-button': { display: 'none' },
  },
  kbd: {
    display: 'inline-grid',
    placeItems: 'center',
    minWidth: 18,
    height: 18,
    padding: '0 4px',
    borderRadius: 4,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-muted)',
    color: 'var(--ui-text-muted)',
    fontFamily: 'var(--font-mono)',
    fontSize: '0.7rem',
    lineHeight: 1,
  },
  layout: {
    display: 'inline-flex',
    flex: 'none',
    padding: 2,
    borderRadius: 'var(--ui-radius)',
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
  },
  layoutButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    height: 26,
    padding: theme.spacing(0, 1),
    border: 'none',
    borderRadius: 3,
    background: 'transparent',
    color: 'var(--ui-text-muted)',
    font: 'inherit',
    fontSize: '0.8125rem',
    cursor: 'pointer',
    '& svg': { fontSize: 16 },
    '&[aria-pressed="true"]': {
      background: 'var(--ui-bg-muted)',
      color: 'var(--ui-text)',
      fontWeight: 600,
    },
    '&:focus-visible': { outline: '2px solid var(--ui-primary)' },
  },
  list: {
    borderRadius: 8,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
    // Scrolls sideways inside itself, never the page, if a table's whole words
    // ever need more room than a phone has — rather than cutting the last
    // column off at the edge without a sign it is there.
    overflowX: 'auto',
  },
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    borderTop: '1px solid var(--ui-border)',
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
  },
  hint: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}));

export const CatalogListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);
  const [params, setParams] = useSearchParams();
  const { filters } = useMemo(() => readFilters(params), [params]);
  const { starredEntities, isStarredEntity, toggleStarredEntity } =
    useStarredEntities();
  const stars = useMemo(
    () => ({ isStarred: isStarredEntity, toggle: toggleStarredEntity }),
    [isStarredEntity, toggleStarredEntity],
  );
  const { error, value } = useCatalogList();
  const [layout, setLayout] = useViewPreference();
  const { allowed: canRegister } = usePermission({
    permission: catalogEntityCreatePermission,
  });
  const filterRef = useRef<HTMLInputElement>(null);

  const reader = useMemo(
    () => ({
      ownership: new Set(value?.ownership ?? []),
      starred: new Set(starredEntities),
    }),
    [value, starredEntities],
  );
  const rows = useMemo(() => value?.rows ?? [], [value]);
  const view = useMemo(
    () => viewOf(rows, filters, reader),
    [rows, filters, reader],
  );

  const update = useCallback(
    (change: Partial<ListFilters>) =>
      setParams(writeFilters({ ...filters, ...change }, params), {
        replace: true,
      }),
    [filters, params, setParams],
  );

  const pathOf = useCallback(
    (row: ListRow) => entityRoute(entityRouteParams(row.entity)),
    [entityRoute],
  );
  const open = useCallback(
    (index: number) => {
      const row = view.shown[index];
      if (row) navigate(pathOf(row));
    },
    [view.shown, navigate, pathOf],
  );

  const active = useListKeyboard({
    count: view.shown.length,
    filterRef,
    onOpen: open,
    text: filters.text,
    scope: JSON.stringify([filters.tab, filters.chip, filters.facets, layout]),
  });

  // The marked entry stays in view as the arrows walk a list longer than the
  // window — the catalog is a hundred entries, and a cursor below the fold is
  // a cursor nobody can see.
  useEffect(() => {
    if (active < 0) return;
    document
      .querySelector<HTMLElement>(`[data-list-row="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [active, layout]);

  const addFacet = (key: FacetKey, facetValue: string) => {
    if (isChosen(filters.facets, key, facetValue)) return;
    update({
      facets: {
        ...filters.facets,
        [key]: [...(filters.facets[key] ?? []), facetValue],
      },
    });
  };
  const removeFacet = (key: FacetKey, facetValue: string) => {
    const rest = (filters.facets[key] ?? []).filter(v => v !== facetValue);
    const facets = { ...filters.facets, [key]: rest };
    if (!rest.length) delete facets[key];
    update({ facets });
  };
  const filtered = Object.keys(filters.facets).length > 0;

  const body = () => {
    if (!value) {
      return error ? <ResponseErrorPanel error={error} /> : <Progress />;
    }
    const showAll = () => update({ tab: 'all' });
    if (view.tabCounts[filters.tab] === 0) {
      if (filters.tab === 'owned') {
        return <EmptyMine ownership={value.ownership} onShowAll={showAll} />;
      }
      if (filters.tab === 'starred')
        return <EmptyStarred onShowAll={showAll} />;
      return <EmptyCatalog />;
    }
    if (!view.inScope.length) {
      return (
        <EmptyScope
          chip={filters.chip}
          filtered={filtered}
          onClear={() =>
            update(filtered ? { facets: {} } : { chip: 'all' as TypeChip })
          }
        />
      );
    }
    if (!view.shown.length) {
      return (
        <EmptyMatch text={filters.text} onClear={() => update({ text: '' })} />
      );
    }
    const List = layout === 'cards' ? ListCards : ListTable;
    return (
      <List
        rows={view.shown}
        active={active}
        pathOf={pathOf}
        onOpen={open}
        stars={stars}
      />
    );
  };

  return (
    <Page themeId="home">
      <Header
        title="Catalog"
        subtitle="Find the services, websites, libraries and APIs that make up the revenexx platform."
      >
        {canRegister && (
          // Ways to other pages, so links to assistive technology too; the
          // button styling would otherwise announce them as buttons.
          // A grid item, as the header's right-hand box expects of its
          // children: the box is a spaced grid, and a bare child sits in its
          // negative margin — on a phone, on top of the subtitle.
          <Grid item className={classes.actions}>
            <LinkButton
              role="link"
              variant="outlined"
              to="/catalog-import"
              startIcon={<SyncIcon />}
            >
              Register existing
            </LinkButton>
            <LinkButton
              role="link"
              variant="contained"
              color="primary"
              to="/create"
              startIcon={<AddIcon />}
            >
              Create
            </LinkButton>
          </Grid>
        )}
      </Header>
      <Content>
        <Tabs
          value={filters.tab}
          onChange={(_, tab: ListTab) => update({ tab })}
          classes={{ root: classes.tabs, indicator: classes.indicator }}
          aria-label="Whose"
        >
          {TABS.map(({ id, label }) => (
            <Tab
              key={id}
              value={id}
              className={classes.tab}
              disableRipple
              label={
                <span>
                  {label}{' '}
                  {value && (
                    <span className={classes.tabCount}>
                      {view.tabCounts[id]}
                    </span>
                  )}
                </span>
              }
            />
          ))}
        </Tabs>

        <div className={classes.toolbar}>
          <FilterChips
            chip={filters.chip}
            chipCounts={value ? view.chipCounts : undefined}
            facets={filters.facets}
            options={key => facetOptions(rows, filters, reader, key)}
            onChip={chip =>
              update({ chip, facets: withoutChipAxis(filters.facets) })
            }
            onAddFacet={addFacet}
            onRemoveFacet={removeFacet}
          />
          <div className={classes.tools}>
            <label className={classes.search}>
              <SearchIcon />
              <input
                ref={filterRef}
                type="search"
                className={classes.searchInput}
                aria-label="Filter the list"
                placeholder="Filter by name, tag, owner…"
                value={filters.text}
                onChange={e => update({ text: e.target.value })}
              />
              <kbd className={classes.kbd} title="Press / to filter">
                /
              </kbd>
            </label>
            <div className={classes.layout} role="group" aria-label="Layout">
              <button
                type="button"
                className={classes.layoutButton}
                aria-pressed={layout === 'table'}
                onClick={() => setLayout('table')}
              >
                <ViewListIcon /> Table
              </button>
              <button
                type="button"
                className={classes.layoutButton}
                aria-pressed={layout === 'cards'}
                onClick={() => setLayout('cards')}
              >
                <ViewModuleIcon /> Cards
              </button>
            </div>
          </div>
        </div>

        <section className={classes.list} aria-label="Catalog entries">
          {body()}
          {value && view.shown.length > 0 && (
            <footer className={classes.footer}>
              <span>
                {view.shown.length} of {view.inScope.length} shown
              </span>
              <span className={classes.hint}>
                <kbd className={classes.kbd}>↑</kbd>
                <kbd className={classes.kbd}>↓</kbd> move ·{' '}
                <kbd className={classes.kbd}>↵</kbd> open
              </span>
            </footer>
          )}
        </section>
      </Content>
    </Page>
  );
};
